import { Alert, AlertIcon, Text } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import { IContestApprovedArtwork, IContestForSelect } from '../../../services/Contests/interfaces'

interface IContestsLabelsModalProps {
  isOpen: boolean
  onClose: () => void
  preSelectedContestName?: string
}

export default function ContestsLabelsModal({ isOpen, onClose, preSelectedContestName }: IContestsLabelsModalProps) {
  const contestsService = useMemo(() => new ContestsService(), [])
  const toast = useAppToast()

  const [contests, setContests] = useState<IContestForSelect[]>([])
  const [selectedContestId, setSelectedContestId] = useState<number>(0)
  const [artworks, setArtworks] = useState<IContestApprovedArtwork[]>([])
  const [loadingContests, setLoadingContests] = useState(false)
  const [loadingArtworks, setLoadingArtworks] = useState(false)
  const [emitting, setEmitting] = useState(false)
  const [contestsError, setContestsError] = useState<string | null>(null)
  const [artworksError, setArtworksError] = useState<string | null>(null)

  const fetchApprovedArtworks = useCallback(
    (id: number) => {
      setArtworks([])
      setArtworksError(null)
      setLoadingArtworks(true)
      contestsService
        .listApprovedArtworks(id)
        .then(setArtworks)
        .catch(() => {
          setArtworksError('Erro ao carregar obras. Tente novamente.')
          toast.error('Erro ao Carregar Obras')
        })
        .finally(() => setLoadingArtworks(false))
    },
    [contestsService, toast]
  )

  useEffect(() => {
    if (!isOpen) return
    setLoadingContests(true)
    setContestsError(null)
    contestsService
      .getAllForSelect()
      .then((response) => {
        setContests(response)
        if (preSelectedContestName) {
          const matchedContest = response.find((c) => c.name === preSelectedContestName)
          if (matchedContest) {
            setSelectedContestId(matchedContest.id)
            fetchApprovedArtworks(matchedContest.id)
          }
        }
      })
      .catch(() => {
        setContestsError('Erro ao carregar concursos. Tente novamente.')
        toast.error('Erro ao Carregar Concursos')
      })
      .finally(() => setLoadingContests(false))
  }, [isOpen, contestsService, toast, preSelectedContestName, fetchApprovedArtworks])

  function handleContestChange(value: string) {
    const id = Number(value)
    setSelectedContestId(id)
    if (!id) {
      setArtworks([])
      setArtworksError(null)
      return
    }
    fetchApprovedArtworks(id)
  }

  function handleEmit() {
    if (!selectedContestId) return
    setEmitting(true)
    contestsService
      .openArtworkLabelsPdf(selectedContestId)
      .then(() => {
        handleClose()
      })
      .catch(() => {
        toast.error('Erro ao gerar etiquetas', 'Não foi possível gerar o PDF de etiquetas. Tente novamente.')
      })
      .finally(() => setEmitting(false))
  }

  function handleClose() {
    setSelectedContestId(0)
    setArtworks([])
    setContestsError(null)
    setArtworksError(null)
    setEmitting(false)
    onClose()
  }

  const hasNoArtworks = selectedContestId > 0 && !loadingArtworks && !artworksError && artworks.length === 0
  const canEmit = selectedContestId > 0 && !loadingArtworks && !artworksError && artworks.length > 0

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Emitir Etiquetas de Obras"
      onSubmit={handleEmit}
      size="compact"
      confirmLabel="Emitir etiquetas"
      isLoading={emitting}
      isConfirmDisabled={!canEmit || emitting}
    >
      {loadingContests ? (
        <LoadingBlock />
      ) : (
        <>
          {contestsError && (
            <Alert status="error" mt={4} borderRadius="md" mb={4}>
              <AlertIcon />
              {contestsError}
            </Alert>
          )}

          <Text mb={2} fontWeight="semibold">
            Selecione o concurso
          </Text>
          <FormSelect
            placeholder="Selecione um concurso"
            value={selectedContestId || ''}
            onChange={handleContestChange}
            isDisabled={contests.length === 0}
            options={contests.map((contest) => ({ value: contest.id, label: contest.name }))}
          />

          {loadingArtworks && <LoadingBlock py={4} />}

          {artworksError && (
            <Alert status="error" mt={4} borderRadius="md">
              <AlertIcon />
              {artworksError}
            </Alert>
          )}

          {hasNoArtworks && (
            <Alert status="warning" mt={4} borderRadius="md">
              <AlertIcon />
              Esse concurso não tem obras aprovadas.
            </Alert>
          )}
        </>
      )}
    </FormModal>
  )
}
